/**
 * STA-3 — Admin › Records Maintenance
 *
 * A general-purpose safety console for diagnosing and purging stray
 * records anywhere in the ERP. Three explicit modes only:
 *   1. Diagnose        — read-only impact report + verdict
 *   2. Preview Purge   — dry-run showing the exact deletion plan
 *   3. Execute Purge   — cascade delete, gated by two confirmation
 *                        tokens + a mandatory reason.
 *
 * No direct "Delete" button. Verdict is always shown before Execute
 * unlocks. Every action is written to an immutable audit log.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import {
  ShieldAlert, Search, PlayCircle, AlertTriangle, CheckCircle2,
  XCircle, Loader2, Trash2, ClipboardList, RefreshCw, Lock,
} from 'lucide-react';
import { axiosInstance } from '../App';

const VERDICT_META = {
  SAFE: {
    cls: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    icon: CheckCircle2,
    label: 'SAFE',
    dot: 'bg-emerald-500',
  },
  WARNING: {
    cls: 'bg-amber-100 text-amber-800 border-amber-300',
    icon: AlertTriangle,
    label: 'WARNING',
    dot: 'bg-amber-500',
  },
  DANGEROUS: {
    cls: 'bg-rose-100 text-rose-800 border-rose-300',
    icon: XCircle,
    label: 'DANGEROUS',
    dot: 'bg-rose-500',
  },
};

const IMPACT_LABELS = {
  GL:              'General Ledger',
  BANK:            'Bank Ledger',
  VAT:             'VAT Projection',
  CASH_PROJECTION: 'Cash Projection',
  FER:             'Financial Event Registry',
  DOCUMENT:        'Source Document',
  PERIOD:          'Accounting Period',
  OTHER:           'Other',
};

function VerdictBadge({ verdict }) {
  const m = VERDICT_META[verdict] || VERDICT_META.WARNING;
  const Icon = m.icon;
  return (
    <span
      data-testid="verdict-badge"
      className={`inline-flex items-center gap-2 border rounded-full
                   px-3 py-1 text-xs font-bold ${m.cls}`}>
      <span className={`w-2 h-2 rounded-full ${m.dot}`} />
      <Icon size={12} /> {m.label}
    </span>
  );
}

function ImpactCard({ area, count, collections }) {
  const filled = count > 0;
  return (
    <div className={`rounded-lg border p-3 ${filled
      ? 'bg-slate-50 border-slate-300'
      : 'bg-white border-slate-200 opacity-60'}`}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">
        {IMPACT_LABELS[area]}
      </div>
      <div className="text-2xl font-bold text-slate-800 mt-1">{count}</div>
      {filled && (
        <div className="text-[11px] text-slate-500 mt-1 truncate">
          {collections.join(', ')}
        </div>
      )}
    </div>
  );
}

export default function AdminRecordsMaintenance() {
  const [targetId, setTargetId]         = useState('');
  const [loading, setLoading]           = useState(false);
  const [report, setReport]             = useState(null);
  const [plan, setPlan]                 = useState(null);
  const [execResult, setExecResult]     = useState(null);
  const [reason, setReason]             = useState('');
  const [confirmWord, setConfirmWord]   = useState('');
  const [confirmSuffix, setConfirmSuffix] = useState('');
  const [auditLog, setAuditLog]         = useState([]);

  const suffix = (targetId || '').trim().slice(-6);

  const canExecute = useMemo(() => {
    if (!plan || plan.total_docs === 0)     return false;
    if (confirmWord !== 'PURGE')             return false;
    if (confirmSuffix !== suffix || !suffix) return false;
    if ((reason || '').trim().length < 10)   return false;
    return true;
  }, [plan, confirmWord, confirmSuffix, suffix, reason]);

  const loadAudit = async () => {
    try {
      const r = await axiosInstance.get('/admin/records/audit-log?limit=25');
      setAuditLog(r.data?.entries || []);
    } catch {
      /* silent */
    }
  };

  useEffect(() => { loadAudit(); }, []);

  const resetPurgeState = () => {
    setPlan(null);
    setExecResult(null);
    setReason('');
    setConfirmWord('');
    setConfirmSuffix('');
  };

  const runDiagnose = async () => {
    if (!targetId.trim()) {
      toast.error('Please enter a target ID.');
      return;
    }
    setLoading(true);
    setReport(null);
    resetPurgeState();
    try {
      const r = await axiosInstance.post('/admin/records/diagnose',
                                          { target_id: targetId.trim() });
      setReport(r.data);
      toast.success(`Diagnosis complete — verdict: ${r.data.impact.verdict}`);
      loadAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Diagnose failed.');
    } finally {
      setLoading(false);
    }
  };

  const runPreview = async () => {
    setLoading(true);
    setPlan(null);
    setExecResult(null);
    try {
      const r = await axiosInstance.post('/admin/records/preview',
                                          { target_id: targetId.trim() });
      setPlan(r.data);
      if (r.data.total_docs === 0) {
        toast.info('No records to delete — nothing to preview.');
      } else {
        toast.success(`Preview built — ${r.data.total_docs} document(s) would be deleted.`);
      }
      loadAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed.');
    } finally {
      setLoading(false);
    }
  };

  const runExecute = async () => {
    if (!canExecute) return;
    if (!window.confirm(
      `This will permanently delete ${plan.total_docs} document(s). Continue?`))
      return;
    setLoading(true);
    setExecResult(null);
    try {
      const r = await axiosInstance.post('/admin/records/execute', {
        target_id:      targetId.trim(),
        reason:         reason.trim(),
        confirm_word:   confirmWord,
        confirm_suffix: confirmSuffix,
      });
      setExecResult(r.data);
      toast.success(`Purge complete — ${r.data.total_deleted} document(s) deleted.`);
      // Refresh diagnosis + audit
      loadAudit();
      // After execute, force re-diagnose to verify clean state
      await runDiagnose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Execute failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <ShieldAlert className="text-rose-600" size={24} />
            <h1 className="text-2xl font-bold text-slate-800">
              Records Maintenance
            </h1>
            <span className="text-[10px] uppercase tracking-wider
                             bg-rose-100 text-rose-700 border border-rose-300
                             px-2 py-0.5 rounded">
              Admin Only
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Diagnose and safely purge stray records anywhere in the ERP.
            Read-only diagnosis; two-token confirmation for execution.
          </p>
        </div>
        <button
          data-testid="refresh-audit-btn"
          onClick={loadAudit}
          className="inline-flex items-center gap-1 text-xs text-slate-600
                     hover:text-slate-900 border border-slate-300 rounded
                     px-2 py-1 bg-white">
          <RefreshCw size={12} /> Refresh Audit
        </button>
      </div>

      {/* ── Input ──────────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
        <label className="block text-xs font-semibold text-slate-600
                          uppercase tracking-wider">
          Target Record ID (UUID or Document ID)
        </label>
        <div className="flex gap-2">
          <input
            data-testid="target-id-input"
            type="text"
            value={targetId}
            onChange={(e) => { setTargetId(e.target.value); resetPurgeState(); setReport(null); }}
            placeholder="e.g. f3dc5e41-50fb-47f0-909e-12fc14f505ca"
            className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm
                       font-mono focus:outline-none focus:ring-2 focus:ring-blue-400" />
          <button
            data-testid="diagnose-btn"
            onClick={runDiagnose}
            disabled={loading || !targetId.trim()}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700
                       disabled:opacity-50 text-white px-4 py-2 rounded text-sm font-medium">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            Diagnose
          </button>
        </div>
        <div className="text-[11px] text-slate-500">
          Diagnose is <b>read-only</b>. It scans every collection, classifies
          the impact, and shows a verdict.
        </div>
      </div>

      {/* ── Diagnosis Report ───────────────────────────────────────── */}
      {report && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-4"
             data-testid="diagnosis-report">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">
                Diagnosis for
              </div>
              <div className="font-mono text-sm text-slate-800">{report.target}</div>
              <div className="text-[11px] text-slate-500 mt-1">
                Scanned <b>{report.collections_scanned}</b> collections —
                found <b>{report.total_documents}</b> document(s) in{' '}
                <b>{Object.keys(report.hits || {}).length}</b> collection(s).
                Entity type: <b>{report.impact.entity_type}</b>.
              </div>
            </div>
            <VerdictBadge verdict={report.impact.verdict} />
          </div>

          {/* Verdict reasons */}
          <div className="bg-slate-50 border border-slate-200 rounded p-3">
            <div className="text-[10px] uppercase tracking-wider
                            text-slate-500 mb-1">Verdict Reasoning</div>
            <ul className="list-disc list-inside text-xs text-slate-700 space-y-0.5">
              {report.impact.reasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          </div>

          {/* Impact grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(report.impact.impact).map(([area, info]) => (
              <ImpactCard key={area} area={area}
                          count={info.count}
                          collections={info.collections} />
            ))}
          </div>

          {/* Collections summary */}
          {Object.keys(report.hits || {}).length > 0 && (
            <div className="border border-slate-200 rounded overflow-hidden">
              <div className="text-[11px] uppercase tracking-wider
                              bg-slate-100 px-3 py-1.5 text-slate-600 font-semibold">
                Collections Touched
              </div>
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-semibold text-slate-500">
                      Collection
                    </th>
                    <th className="text-left px-3 py-1.5 font-semibold text-slate-500">
                      Docs
                    </th>
                    <th className="text-left px-3 py-1.5 font-semibold text-slate-500">
                      Matched Via
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(report.hits).map(([coll, matches]) => (
                    <tr key={coll} className="border-b border-slate-100">
                      <td className="px-3 py-1.5 font-mono">{coll}</td>
                      <td className="px-3 py-1.5">{matches.length}</td>
                      <td className="px-3 py-1.5 text-slate-500 text-[11px]">
                        {[...new Set(matches.flatMap(m => m.matched_via || []))]
                          .join(', ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Preview button */}
          <div className="flex items-center gap-2 pt-2">
            <button
              data-testid="preview-purge-btn"
              onClick={runPreview}
              disabled={loading || report.total_documents === 0}
              className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900
                         disabled:opacity-50 text-white px-4 py-2 rounded text-sm font-medium">
              <PlayCircle size={14} /> Preview Execution (Dry Run)
            </button>
            {report.total_documents === 0 && (
              <span className="text-xs text-slate-500">
                Nothing to delete — target not found.
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Preview Plan ───────────────────────────────────────────── */}
      {plan && plan.total_docs > 0 && (
        <div className="bg-white border-2 border-amber-300 rounded-lg p-4 space-y-4"
             data-testid="preview-plan">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-600" />
            <h3 className="text-sm font-bold text-slate-800">
              Deletion Plan — {plan.total_docs} document(s)
            </h3>
            <span className="text-[10px] bg-amber-100 text-amber-700
                             border border-amber-300 px-2 py-0.5 rounded uppercase">
              Dry Run
            </span>
          </div>

          <div className="border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-1.5 font-semibold text-slate-500">
                    Collection
                  </th>
                  <th className="text-left px-3 py-1.5 font-semibold text-slate-500">
                    Doc IDs
                  </th>
                  <th className="text-left px-3 py-1.5 font-semibold text-slate-500">
                    Count
                  </th>
                </tr>
              </thead>
              <tbody>
                {plan.steps.map((s) => (
                  <tr key={s.collection} className="border-b border-slate-100">
                    <td className="px-3 py-1.5 font-mono">{s.collection}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-slate-500">
                      {s.doc_ids.slice(0, 3).join(', ')}
                      {s.doc_ids.length > 3 && ` … +${s.doc_ids.length - 3}`}
                    </td>
                    <td className="px-3 py-1.5 font-bold text-slate-700">{s.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Execute Gate */}
          <div className="border-t border-slate-200 pt-4 space-y-3">
            <div className="flex items-center gap-2 text-rose-700">
              <Lock size={14} />
              <span className="text-sm font-bold">
                Two-Token Confirmation Required
              </span>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-600
                                uppercase tracking-wider mb-1">
                Reason (min 10 characters)
              </label>
              <textarea
                data-testid="reason-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="e.g. Erroneous test bill created during migration; no downstream ledger entries."
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm
                           focus:outline-none focus:ring-2 focus:ring-rose-400" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-slate-600
                                  uppercase tracking-wider mb-1">
                  Type the word <code className="bg-slate-100 px-1
                                        rounded text-rose-700">PURGE</code>
                </label>
                <input
                  data-testid="confirm-word-input"
                  type="text"
                  value={confirmWord}
                  onChange={(e) => setConfirmWord(e.target.value)}
                  placeholder="PURGE"
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm
                             font-mono focus:outline-none focus:ring-2 focus:ring-rose-400" />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-slate-600
                                  uppercase tracking-wider mb-1">
                  Last 6 chars of ID: <code className="bg-slate-100 px-1
                                        rounded text-rose-700">{suffix}</code>
                </label>
                <input
                  data-testid="confirm-suffix-input"
                  type="text"
                  value={confirmSuffix}
                  onChange={(e) => setConfirmSuffix(e.target.value)}
                  placeholder={suffix}
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm
                             font-mono focus:outline-none focus:ring-2 focus:ring-rose-400" />
              </div>
            </div>

            <button
              data-testid="execute-purge-btn"
              onClick={runExecute}
              disabled={!canExecute || loading}
              className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700
                         disabled:opacity-40 disabled:cursor-not-allowed
                         text-white px-4 py-2 rounded text-sm font-bold">
              {loading ? <Loader2 size={14} className="animate-spin" />
                       : <Trash2 size={14} />}
              Execute Purge
            </button>
          </div>
        </div>
      )}

      {/* ── Execution Result ───────────────────────────────────────── */}
      {execResult && (
        <div className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-4 space-y-2"
             data-testid="execution-result">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={16} className="text-emerald-600" />
            <h3 className="text-sm font-bold text-emerald-800">
              Purge Complete — {execResult.total_deleted} document(s) deleted
            </h3>
          </div>
          <div className="text-xs text-emerald-800 space-y-0.5">
            {Object.entries(execResult.deleted).map(([coll, count]) => (
              <div key={coll}>
                <span className="font-mono">{coll}</span>: {count} deleted
              </div>
            ))}
          </div>
          {execResult.errors && execResult.errors.length > 0 && (
            <div className="text-xs text-rose-700 mt-2">
              <b>Errors:</b> {JSON.stringify(execResult.errors)}
            </div>
          )}
        </div>
      )}

      {/* ── Audit Log ──────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-lg p-4"
           data-testid="audit-log">
        <div className="flex items-center gap-2 mb-3">
          <ClipboardList size={16} className="text-slate-600" />
          <h3 className="text-sm font-bold text-slate-800">
            Recent Maintenance Events
          </h3>
          <span className="text-[10px] text-slate-500">
            ({auditLog.length} entries)
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-2 py-1.5 font-semibold text-slate-500">When</th>
                <th className="text-left px-2 py-1.5 font-semibold text-slate-500">User</th>
                <th className="text-left px-2 py-1.5 font-semibold text-slate-500">Mode</th>
                <th className="text-left px-2 py-1.5 font-semibold text-slate-500">Target</th>
                <th className="text-left px-2 py-1.5 font-semibold text-slate-500">Verdict</th>
                <th className="text-left px-2 py-1.5 font-semibold text-slate-500">Deleted</th>
                <th className="text-left px-2 py-1.5 font-semibold text-slate-500">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {auditLog.length === 0 && (
                <tr><td colSpan={7}
                        className="px-2 py-3 text-center text-slate-400">
                  No maintenance events yet.
                </td></tr>
              )}
              {auditLog.map((e) => (
                <tr key={e.id} className="border-b border-slate-100">
                  <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">
                    {e.created_at ? new Date(e.created_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-2 py-1.5">{e.user_email || '—'}</td>
                  <td className="px-2 py-1.5">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                      e.mode === 'execute' ? 'bg-rose-100 text-rose-700' :
                      e.mode === 'preview' ? 'bg-amber-100 text-amber-700' :
                                             'bg-blue-100 text-blue-700'
                    }`}>{e.mode}</span>
                  </td>
                  <td className="px-2 py-1.5 font-mono text-[10px]">
                    {e.target_id?.slice(0, 8)}…{e.target_id?.slice(-6)}
                  </td>
                  <td className="px-2 py-1.5">
                    {e.verdict ? <VerdictBadge verdict={e.verdict} /> : '—'}
                  </td>
                  <td className="px-2 py-1.5 font-bold">{e.docs_deleted || 0}</td>
                  <td className="px-2 py-1.5">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                      e.outcome === 'ok'      ? 'bg-emerald-100 text-emerald-700' :
                      e.outcome === 'blocked' ? 'bg-amber-100 text-amber-700' :
                                                 'bg-rose-100 text-rose-700'
                    }`}>{e.outcome}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
